'use client';
import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import AppLogo from '@/components/ui/AppLogo';
import { Wrench, FileText, Calendar, Shield } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const features = [
  { icon: Wrench, label: 'Works Orders', desc: 'Full lifecycle with file uploads & risk assessments' },
  { icon: FileText, label: 'Quote Pipeline', desc: 'One-click conversion to works orders' },
  { icon: Calendar, label: 'Scheduling', desc: 'Engineer dispatch & site work management' },
  { icon: Shield, label: 'Purchase Orders', desc: 'Approval workflows & supplier management' },
];

type Mode = 'login' | 'signup' | 'forgot';

export default function AuthClient() {
  const [mode, setMode] = useState<Mode>('login');

  return (
    <div className="min-h-screen flex bg-[#0a0f1e]">
      {/* ── Left panel ── */}
      <div className="hidden lg:flex lg:w-[52%] relative flex-col overflow-hidden">
        {/* Animated gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 20% 10%, rgba(30,64,175,0.55) 0%, transparent 60%), radial-gradient(ellipse 60% 80% at 80% 80%, rgba(15,118,110,0.35) 0%, transparent 55%), radial-gradient(ellipse 70% 50% at 50% 50%, rgba(10,15,30,1) 0%, transparent 100%)',
            backgroundColor: '#0a0f1e',
          }}
        />

        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />

        {/* Floating orbs */}
        <div
          className="absolute top-[15%] left-[10%] w-64 h-64 rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, #3b82f6, transparent)' }}
        />
        <div
          className="absolute bottom-[20%] right-[5%] w-80 h-80 rounded-full opacity-15 blur-3xl"
          style={{ background: 'radial-gradient(circle, #14b8a6, transparent)' }}
        />

        {/* Logo */}
        <div className="relative z-10 p-10 pb-0">
          <AppLogo size={600} />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center flex-1 px-12 pb-16">
          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs text-white/60 font-500 tracking-wide">Engineering Operations Platform</span>
            </div>

            <h1 className="text-[2.6rem] font-800 text-white leading-[1.15] mb-4 tracking-tight">
              Run your entire
              <br />
              <span
                style={{
                  background: 'linear-gradient(135deg, #60a5fa 0%, #34d399 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                engineering business
              </span>
              <br />
              from one place.
            </h1>

            <p className="text-white/50 text-base leading-relaxed mb-10">
              The complete CRM built for engineering companies — works orders, quotes, scheduling, and compliance in a single workspace.
            </p>

            {/* Feature list */}
            <div className="grid grid-cols-2 gap-3">
              {features.map((f) => {
                const Icon = f.icon;
                return (
                  <div
                    key={`feat-${f.label}`}
                    className="group flex flex-col gap-2 p-4 rounded-xl border border-white/8 bg-white/4 hover:bg-white/8 hover:border-white/15 transition-all duration-200"
                  >
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                      <Icon size={15} className="text-blue-300" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-600 leading-tight">{f.label}</p>
                      <p className="text-white/40 text-xs mt-0.5 leading-snug">{f.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="relative z-10 px-12 py-6 border-t border-white/8">
          <div className="flex items-center gap-6">
            <span className="text-white/30 text-xs">GDPR Compliant</span>
            <span className="w-px h-3 bg-white/15" />
            <span className="text-white/30 text-xs">UK Engineering Focused</span>
            <span className="w-px h-3 bg-white/15" />
            <span className="text-white/30 text-xs">Secure &amp; Encrypted</span>
          </div>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="flex-1 flex flex-col items-center justify-center relative bg-[#f8f9fc] min-h-screen">
        {/* Subtle top-right decoration */}
        <div
          className="absolute top-0 right-0 w-72 h-72 opacity-30 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at 100% 0%, rgba(59,130,246,0.12), transparent 70%)',
          }}
        />

        {/* Mobile logo */}
        <div className="lg:hidden flex justify-center mb-8 mt-10">
          <AppLogo size={480} />
        </div>

        <div className="w-full max-w-[420px] px-6 py-8">
          {/* Card */}
          <div className="bg-white rounded-2xl shadow-[0_4px_32px_rgba(0,0,0,0.08)] border border-gray-100 p-8">
            {/* Tab toggle */}
            {mode !== 'forgot' && (
              <div className="flex bg-gray-100 rounded-xl p-1 mb-7">
                <button
                  onClick={() => setMode('login')}
                  className={`flex-1 py-2 text-sm font-600 rounded-lg transition-all duration-200 ${
                    mode === 'login' ?'bg-white shadow-sm text-gray-900' :'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => setMode('signup')}
                  className={`flex-1 py-2 text-sm font-600 rounded-lg transition-all duration-200 ${
                    mode === 'signup' ?'bg-white shadow-sm text-gray-900' :'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Create Account
                </button>
              </div>
            )}

            {mode === 'login' && (
              <LoginForm
                onSwitchToSignup={() => setMode('signup')}
                onForgotPassword={() => setMode('forgot')}
              />
            )}
            {mode === 'signup' && <SignUpForm onSwitchToLogin={() => setMode('login')} />}
            {mode === 'forgot' && <ForgotPasswordForm onBack={() => setMode('login')} />}
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-gray-400 mt-6">
            By continuing you agree to our{' '}
            <a href="#" className="text-gray-500 hover:text-gray-700 underline underline-offset-2">
              Terms
            </a>{' '}
            &amp;{' '}
            <a href="#" className="text-gray-500 hover:text-gray-700 underline underline-offset-2">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}